import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useLinkedInOrganizations,
  useTikTokCreatorInfo,
  useRedditSubreddits,
  useRedditFlairs,
  useYoutubePlaylists,
  useFacebookPages,
  useGmbLocations,
  useValidateSubreddit,
  YOUTUBE_CATEGORIES,
} from "@/hooks/use-zernio-options";

interface KnobProps {
  brandId: string;
  value: any;
  onChange: (next: any) => void;
  disabled?: boolean;
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <label className="text-xs font-medium uppercase text-muted-foreground">{children}</label>
  );
}

export function LinkedInOrganizationKnob({ brandId, value, onChange, disabled }: KnobProps) {
  const { t } = useTranslation("compose");
  const q = useLinkedInOrganizations(brandId, !disabled);
  const orgs = q.data?.organizations ?? [];
  return (
    <div className="space-y-1">
      <Label>{t("knobs.linkedInOrg")}</Label>
      <select
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className="block w-full rounded border px-3 py-2 text-sm"
      >
        <option value="">{q.isLoading ? t("knobs.loading") : t("knobs.linkedInOrgDefault")}</option>
        {orgs.map((o) => (
          <option key={o.urn} value={o.urn}>
            {o.name}
          </option>
        ))}
      </select>
    </div>
  );
}

export function TikTokPrivacyKnob({
  brandId,
  value,
  onChange,
  disabled,
  mediaType = "video",
}: KnobProps & { mediaType?: "video" | "photo" }) {
  const { t } = useTranslation("compose");
  const q = useTikTokCreatorInfo(brandId, !disabled, mediaType);
  const levels = q.data?.privacyLevels ?? [];
  const creator = q.data?.creator;
  return (
    <div className="space-y-1">
      <Label>{t("knobs.tiktokPrivacy")}</Label>
      <select
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled || levels.length === 0}
        className="block w-full rounded border px-3 py-2 text-sm"
      >
        <option value="">{q.isLoading ? t("knobs.loading") : t("knobs.tiktokPrivacyDefault")}</option>
        {levels.map((l) => (
          <option key={l.value} value={l.value}>
            {l.label} ({l.value})
          </option>
        ))}
      </select>
      {creator && (
        <div className="text-[10px] text-muted-foreground">
          @{creator.nickname}
          {creator.isVerified ? ` ${t("knobs.tiktokVerified")}` : ""}
          {creator.canPostMore === false ? ` ${t("knobs.tiktokDailyLimit")}` : ""}
        </div>
      )}
    </div>
  );
}

export function RedditSubredditKnob({ brandId, value, onChange, disabled }: KnobProps) {
  const { t } = useTranslation("compose");
  const [q, setQ] = useState("");
  const search = useRedditSubreddits(brandId, q);
  const subreddits = search.data?.subreddits ?? [];
  const validation = useValidateSubreddit(brandId, (value as string) ?? "");
  const v = validation.data;
  return (
    <div className="space-y-1">
      <Label>{t("knobs.subreddit")}</Label>
      <input
        type="text"
        value={(value as string) || ""}
        disabled={disabled}
        placeholder={t("knobs.subredditPlaceholder")}
        onChange={(e) => {
          onChange(e.target.value);
          setQ(e.target.value);
        }}
        className="block w-full rounded border px-3 py-2 text-sm"
      />
      {q.length >= 2 && subreddits.length > 0 && (
        <div className="rounded border bg-card max-h-44 overflow-y-auto text-xs">
          {subreddits.map((s) => (
            <button
              key={s.name}
              type="button"
              onClick={() => {
                onChange(s.name);
                setQ("");
              }}
              className="w-full text-left p-2 hover:bg-muted flex items-center justify-between gap-2"
            >
              <div>
                <div className="font-medium">r/{s.name}</div>
                {s.title && <div className="text-muted-foreground truncate">{s.title}</div>}
              </div>
              {s.subscribers != null && (
                <span className="text-muted-foreground text-[10px]">
                  {s.subscribers >= 1000 ? `${Math.round(s.subscribers / 1000)}k` : s.subscribers}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
      {/* Live validation hints once a value is set */}
      {value && v && !validation.isLoading && (
        <div className="text-[10px] space-y-0.5">
          {v.exists === false ? (
            <div className="text-rose-600">{t("knobs.subredditNotFound", { name: value as string })}</div>
          ) : (
            <>
              {v.subreddit?.type === "private" && (
                <div className="text-rose-600">{t("knobs.subredditPrivate")}</div>
              )}
              {v.subreddit?.type === "restricted" && (
                <div className="text-amber-600">{t("knobs.subredditRestricted")}</div>
              )}
              {v.subreddit?.isNSFW && (
                <div className="text-amber-600">{t("knobs.subredditNsfw")}</div>
              )}
              {v.subreddit?.allowImages === false && (
                <div className="text-amber-600">{t("knobs.subredditNoImages")}</div>
              )}
              {v.subreddit?.allowVideos === false && (
                <div className="text-amber-600">{t("knobs.subredditNoVideos")}</div>
              )}
              {v.subreddit?.subscribers != null && (
                <div className="text-muted-foreground">
                  {v.subreddit.subscribers.toLocaleString()} subscribers
                  {v.subreddit?.type && ` · ${v.subreddit.type}`}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export function FacebookPageKnob({ brandId, value, onChange, disabled }: KnobProps) {
  const { t } = useTranslation("compose");
  const q = useFacebookPages(brandId, !disabled);
  const pages = q.data?.pages ?? [];
  return (
    <div className="space-y-1">
      <Label>{t("knobs.facebookPage")}</Label>
      <select
        value={(value as string) || (q.data?.selectedPageId ?? "")}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled || pages.length === 0}
        className="block w-full rounded border px-3 py-2 text-sm"
      >
        <option value="">{q.isLoading ? t("knobs.loading") : t("knobs.facebookPageDefault")}</option>
        {pages.map((p) => (
          <option key={p.id} value={p.id}>
            {p.name}
            {p.fanCount != null ? ` · ${p.fanCount.toLocaleString()} fans` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

export function GmbLocationKnob({ brandId, value, onChange, disabled }: KnobProps) {
  const { t } = useTranslation("compose");
  const q = useGmbLocations(brandId, !disabled);
  const locations = q.data?.locations ?? [];
  return (
    <div className="space-y-1">
      <Label>{t("knobs.gmbLocation")}</Label>
      <select
        value={(value as string) || (q.data?.selectedLocationId ?? "")}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled || locations.length === 0}
        className="block w-full rounded border px-3 py-2 text-sm"
      >
        <option value="">{q.isLoading ? t("knobs.loading") : t("knobs.gmbLocationDefault")}</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
            {l.address ? ` — ${l.address}` : ""}
          </option>
        ))}
      </select>
    </div>
  );
}

export function RedditFlairKnob({
  brandId,
  value,
  onChange,
  disabled,
  subreddit,
}: KnobProps & { subreddit: string }) {
  const { t } = useTranslation("compose");
  const q = useRedditFlairs(brandId, subreddit);
  const flairs = q.data?.flairs ?? [];
  return (
    <div className="space-y-1">
      <Label>{t("knobs.flair")}</Label>
      <select
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled || !subreddit}
        className="block w-full rounded border px-3 py-2 text-sm"
      >
        <option value="">{!subreddit ? t("knobs.flairPickSubfirst") : t("knobs.flairNone")}</option>
        {flairs.map((f) => (
          <option key={f.id} value={f.id}>
            {f.text}
          </option>
        ))}
      </select>
    </div>
  );
}

export function YoutubeCategoryKnob({ value, onChange, disabled }: KnobProps) {
  const { t } = useTranslation("compose");
  return (
    <div className="space-y-1">
      <Label>{t("knobs.ytCategory")}</Label>
      <select
        value={(value as string) || "22"}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        className="block w-full rounded border px-3 py-2 text-sm"
      >
        {YOUTUBE_CATEGORIES.map((c) => (
          <option key={c.id} value={c.id}>
            {c.label} ({c.id})
          </option>
        ))}
      </select>
    </div>
  );
}

export function YoutubePlaylistKnob({ brandId, value, onChange, disabled }: KnobProps) {
  const { t } = useTranslation("compose");
  const q = useYoutubePlaylists(brandId, !disabled);
  const playlists = q.data?.playlists ?? [];
  return (
    <div className="space-y-1">
      <Label>{t("knobs.ytPlaylist")}</Label>
      <select
        value={(value as string) || ""}
        onChange={(e) => onChange(e.target.value || null)}
        disabled={disabled}
        className="block w-full rounded border px-3 py-2 text-sm"
      >
        <option value="">{q.isLoading ? t("knobs.loading") : t("knobs.ytPlaylistNone")}</option>
        {playlists.map((p) => (
          <option key={p.id} value={p.id}>
            {p.title}
          </option>
        ))}
      </select>
    </div>
  );
}
